import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import BrokerageDetail from "@/components/BrokerageDetail";

/**
 * BrokeragePeekModal
 * ------------------
 * Opens a brokerage's full detail sheet on top of whatever the user is
 * currently doing (typically a Realtor edit dialog) so they can hop
 * back and forth between a realtor and their brokerage without losing
 * their spot. Symmetric partner to RealtorPeekModal.
 *
 * Uses a Sheet (right-side slide-in) to mirror the primary /brokerages
 * detail experience — feels like the same view, just entered from
 * inside a realtor card. Radix stacks Sheet/Dialog layers correctly, so
 * the parent Realtor dialog stays open underneath.
 */
export default function BrokeragePeekModal({ brokerageId, onClose, onUpdated }) {
  const [brokerage, setBrokerage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!brokerageId) return;
    let cancelled = false;
    setLoading(true);
    api
      .get(`/brokerages/${brokerageId}`)
      .then(({ data }) => {
        if (!cancelled) setBrokerage(data);
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error("Couldn't open brokerage", {
            description: formatApiError(e),
          });
          onClose && onClose();
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [brokerageId, onClose]);

  if (!brokerageId) return null;

  return (
    <Sheet open={!!brokerageId} onOpenChange={(o) => !o && onClose && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto"
        data-testid="brokerage-peek-sheet"
      >
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            Brokerage Details
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4">
          {loading || !brokerage ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading brokerage…
            </div>
          ) : (
            <BrokerageDetail
              brokerage={brokerage}
              onUpdated={(fresh) => {
                setBrokerage(fresh);
                if (onUpdated) onUpdated(fresh);
              }}
              onClose={onClose}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
